import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { taskPhotoService } from '../../../services/taskPhotoService';
import PhotoDetailModal from '../../site-photo-gallery/components/PhotoDetailModal';

const PHOTO_LIMIT = 6;

// Per-thumbnail signed URL fetch. Mirrors PhotoCard in /site-photo-gallery so
// the dashboard preview behaves the same way (loading spinner → image, or
// fallback icon if the signed URL fails).
const Thumbnail = ({ photo, onClick }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!photo?.filePath) return undefined;
    setLoading(true);
    setImgError(false);
    taskPhotoService?.getSignedUrl(photo?.filePath, 3600)?.then(({ url, error }) => {
      if (cancelled) return;
      if (error) {
        console.error('[RecentSitePhotosPanel] thumbnail signed URL failed:', { photoId: photo?.id, code: error?.code, message: error?.message });
        setImgError(true);
      }
      setImgUrl(url || null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [photo?.filePath, photo?.id]);

  return (
    <button
      type="button"
      onClick={() => onClick?.(photo, imgUrl)}
      className="group relative aspect-square rounded-lg overflow-hidden bg-muted border border-border hover:border-accent/60 transition-all"
      title={photo?.caption || photo?.fileName || 'Photo'}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-border border-t-accent rounded-full animate-spin" />
        </div>
      )}
      {imgError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
          <Icon name="ImageOff" size={18} />
          <span className="text-[10px]">Failed</span>
        </div>
      )}
      {imgUrl && !imgError && (
        <img
          src={imgUrl}
          alt={photo?.caption || 'Site photo'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={() => setImgError(true)}
        />
      )}
      {/* Bottom gradient with uploader name on hover */}
      <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="text-[10px] text-white font-medium truncate">{photo?.uploader?.full_name || 'Unknown'}</p>
      </div>
    </button>
  );
};

const RecentSitePhotosPanel = ({ siteId, siteName, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    if (!siteId) {
      setPhotos([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    taskPhotoService?.getRecentBySite(siteId, PHOTO_LIMIT)?.then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        setError(err?.code === 'PGRST301' || err?.code === '42501'
          ? `Permission denied loading photos: ${err?.message ?? 'unknown error'}`
          : `Failed to load photos: ${err?.message ?? 'unknown error'}`);
      }
      setPhotos(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId, refreshKey]);

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground font-heading truncate">Recent Photos</h3>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {siteName ? `Latest uploads for ${siteName}` : 'Latest uploads for this site'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/site-photo-gallery')}
          className="flex items-center gap-1 text-xs font-medium text-accent hover:underline shrink-0"
        >
          View all
          <Icon name="ArrowRight" size={12} />
        </button>
      </div>

      <div className="p-4">
        {error && (
          <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-3">
            <Icon name="AlertCircle" size={14} className="text-destructive mt-0.5 shrink-0" />
            <p className="text-xs text-destructive break-words">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {Array.from({ length: PHOTO_LIMIT })?.map((_, i) => (
              <div key={i} className="aspect-square rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : photos?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-2">
              <Icon name="Images" size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No photos yet for this site</p>
            <p className="text-xs text-muted-foreground mt-1">Use the Add Photo quick action above to upload one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {photos?.map(p => (
              <Thumbnail key={p?.id} photo={p} onClick={() => setSelectedPhoto(p)} />
            ))}
          </div>
        )}
      </div>

      {selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          canDelete={false}
          canManage={false}
        />
      )}
    </div>
  );
};

export default RecentSitePhotosPanel;
